import {
  eachDayOfInterval,
  format,
  getYear,
  isAfter,
  isBefore,
  parseISO,
  startOfDay,
} from 'date-fns';
import type { CalendarEvent } from '@/api/events';
import type { MeetingRoom } from '@/api/meetings';
import { formatReminderSummary } from '@/lib/eventReminders';
import type { CalendarNote } from '@/api/notes';
import type { CalendarTask } from '@/api/tasks';
import type { OrgHoliday, OrgScheduleSettings, OrgWideEvent } from '@/api/org';

export type DayBadgeKind =
  | 'event'
  | 'meeting'
  | 'holiday'
  | 'org-event'
  | 'schedule'
  | 'note'
  | 'task'
  | 'pms-task'
  | 'pms-project';

export type DayBadge = {
  id: string;
  kind: DayBadgeKind;
  label: string;
};

export type AgendaItemKind = DayBadgeKind;

export type DayAgendaItem = {
  id: string;
  kind: AgendaItemKind;
  title: string;
  allDay: boolean;
  startsAt: string | null;
  endsAt: string | null;
  timeLabel: string;
  details?: string | null;
  meetingMeta?: {
    roomId: string;
    status: string | null;
    durationMinutes: number;
  } | null;
  /** Set for personal calendar events — reminder schedule summary. */
  remindersSummary?: string | null;
};

type DateRange = { start: Date; end: Date };

export function buildEventBadgesByDay(eventsByDay: Map<string, CalendarEvent[]>): Map<string, DayBadge[]> {
  const map = new Map<string, DayBadge[]>();

  for (const [key, events] of eventsByDay) {
    map.set(
      key,
      events.map((event) => ({
        id: event.id,
        kind: 'event',
        label: event.title,
      }))
    );
  }

  return map;
}

export function buildHolidayBadgesByDay(
  holidays: OrgHoliday[],
  visibleRange: DateRange
): Map<string, DayBadge[]> {
  const map = new Map<string, DayBadge[]>();

  for (const holiday of holidays) {
    const ranges = buildHolidayDateRanges(holiday, visibleRange);
    for (const range of ranges) {
      addBadgesForEachDay(
        map,
        range,
        visibleRange,
        () => ({
          id: `${holiday.id}-${range.start.toISOString()}`,
          kind: 'holiday',
          label: holiday.name,
        })
      );
    }
  }

  return map;
}

export function buildOrgEventBadgesByDay(
  events: OrgWideEvent[],
  visibleRange: DateRange
): Map<string, DayBadge[]> {
  const map = new Map<string, DayBadge[]>();

  for (const event of events) {
    const starts = startOfDay(parseISO(event.startsAt));
    const ends = startOfDay(parseISO(event.endsAt));
    addBadgesForEachDay(
      map,
      { start: starts, end: ends },
      visibleRange,
      (day) => ({
        id: `${event.id}-${day.toISOString()}`,
        kind: 'org-event',
        label: event.title,
      })
    );
  }

  return map;
}

export function buildScheduleBadgesByDay(
  weeklyHours: OrgScheduleSettings['weeklyHours'],
  dates: Date[]
): Map<string, DayBadge[]> {
  const map = new Map<string, DayBadge[]>();
  if (!weeklyHours || typeof weeklyHours !== 'object') return map;

  for (const date of dates) {
    const key = format(startOfDay(date), 'yyyy-MM-dd');
    const slots = extractScheduleSlots(weeklyHours, date);
    if (slots.length === 0) continue;
    map.set(
      key,
      slots.map((slot, index) => ({
        id: `schedule-${key}-${index}`,
        kind: 'schedule',
        label: slot.label,
      }))
    );
  }

  return map;
}

export function buildNoteBadgesByDay(
  notes: CalendarNote[]
): Map<string, DayBadge[]> {
  const map = new Map<string, DayBadge[]>();
  const sorted = [...notes].sort((a, b) => {
    const aTime = a.happensAt ? parseISO(a.happensAt).getTime() : Number.MAX_SAFE_INTEGER;
    const bTime = b.happensAt ? parseISO(b.happensAt).getTime() : Number.MAX_SAFE_INTEGER;
    return aTime - bTime;
  });

  for (const note of sorted) {
    if (!note.happensAt) continue;
    const key = format(startOfDay(parseISO(note.happensAt)), 'yyyy-MM-dd');
    const label = note.title ?? 'Note';
    const existing = map.get(key) ?? [];
    existing.push({
      id: note.id,
      kind: 'note',
      label,
    });
    map.set(key, existing);
  }
  return map;
}

export function mergeDayBadges(...maps: Map<string, DayBadge[]>[]): Map<string, DayBadge[]> {
  const merged = new Map<string, DayBadge[]>();
  for (const map of maps) {
    for (const [key, badges] of map) {
      const existing = merged.get(key) ?? [];
      merged.set(key, [...existing, ...badges]);
    }
  }
  return merged;
}

export function buildDayAgendaItems(params: {
  date: Date | null;
  dayEvents: CalendarEvent[];
  dayMeetings: MeetingRoom[];
  dayOrgEvents: OrgWideEvent[];
  dayHolidays: OrgHoliday[];
  dayNotes: CalendarNote[];
  dayTasks: CalendarTask[];
  weeklyHours: OrgScheduleSettings['weeklyHours'];
}): DayAgendaItem[] {
  const { date, dayEvents, dayMeetings, dayOrgEvents, dayHolidays, dayNotes, dayTasks, weeklyHours } = params;
  if (!date) return [];

  const scheduleSlots = extractScheduleSlots(weeklyHours, date);

  const eventItems = dayEvents.map<DayAgendaItem>((event) => ({
    id: event.id,
    kind: 'event',
    title: event.title,
    allDay: event.allDay,
    startsAt: event.startsAt,
    endsAt: event.endsAt,
    timeLabel: event.allDay
      ? 'All day'
      : `${format(parseISO(event.startsAt), 'p')} - ${format(parseISO(event.endsAt), 'p')}`,
    details: event.description ?? event.location ?? null,
    remindersSummary: formatReminderSummary(event.reminderOffsetsMinutes),
  }));

  const orgEventItems = dayOrgEvents.map<DayAgendaItem>((event) => ({
    id: event.id,
    kind: 'org-event',
    title: event.title,
    allDay: event.allDay,
    startsAt: event.startsAt,
    endsAt: event.endsAt,
    timeLabel: event.allDay ? 'All day' : `${format(parseISO(event.startsAt), 'p')} - ${format(parseISO(event.endsAt), 'p')}`,
    details: event.description ?? event.venue ?? null,
  }));

  const meetingItems = dayMeetings.map<DayAgendaItem>((meeting) => {
    const startsAt = meeting.scheduledAt ?? meeting.createdAt;
    const endSource = meeting.scheduledAt ?? meeting.createdAt;
    const durationMinutes = Number(meeting.duration ?? 60);
    const parsedStart = parseISO(startsAt);
    const parsedEnd = parseISO(endSource);
    const endWithDuration = new Date(parsedEnd.getTime() + durationMinutes * 60 * 1000);
    const status = meeting.status ? `Status: ${meeting.status}` : null;

    return {
      id: meeting.roomId,
      kind: 'meeting',
      title: meeting.name || 'Untitled meeting',
      allDay: false,
      startsAt,
      endsAt: endWithDuration.toISOString(),
      timeLabel: `${format(parsedStart, 'p')} - ${format(endWithDuration, 'p')}`,
      details: [`Room: ${meeting.roomId}`, status].filter(Boolean).join(' • '),
      meetingMeta: {
        roomId: meeting.roomId,
        status: meeting.status ?? null,
        durationMinutes,
      },
    };
  });

  const holidayItems = dayHolidays.map<DayAgendaItem>((holiday) => ({
    id: holiday.id,
    kind: 'holiday',
    title: holiday.name,
    allDay: true,
    startsAt: null,
    endsAt: null,
    timeLabel: 'All day',
    details: holiday.description,
  }));

  const noteItems = dayNotes
    .filter((n) => Boolean(n.happensAt))
    .map<DayAgendaItem>((note) => ({
      id: note.id,
      kind: 'note',
      title: note.title ?? 'Note',
      allDay: false,
      startsAt: note.happensAt,
      endsAt: null,
      timeLabel: note.happensAt ? format(parseISO(note.happensAt), 'p') : 'Note',
      details: `${note.pinned ? 'Pinned. ' : ''}${note.body}`,
    }));

  const taskItems = dayTasks.map<DayAgendaItem>((task) => {
    const due = task.dueAt ? parseISO(task.dueAt) : null;
    const hasDueAt = Boolean(due);
    return {
      id: task.id,
      kind: 'task',
      title: task.title,
      allDay: !hasDueAt,
      startsAt: task.dueAt,
      endsAt: null,
      timeLabel: hasDueAt ? format(due!, 'p') : 'Task',
      details:
        task.priority && task.priority !== 'NONE'
          ? `Priority: ${task.priority}`
          : null,
    };
  });

  const scheduleItems = scheduleSlots.map<DayAgendaItem>((slot, index) => ({
    id: `schedule-${format(date, 'yyyy-MM-dd')}-${index}`,
    kind: 'schedule',
    title: slot.title,
    allDay: false,
    startsAt: null,
    endsAt: null,
    timeLabel: slot.label,
    details: null,
  }));

  return [...eventItems, ...meetingItems, ...orgEventItems, ...holidayItems, ...noteItems, ...taskItems, ...scheduleItems].sort(compareAgendaItems);
}

export function getDayOrgEvents(date: Date | null, orgEventsByDay: Map<string, OrgWideEvent[]>): OrgWideEvent[] {
  if (!date) return [];
  return orgEventsByDay.get(format(startOfDay(date), 'yyyy-MM-dd')) ?? [];
}

export function getDayHolidays(date: Date | null, holidaysByDay: Map<string, OrgHoliday[]>): OrgHoliday[] {
  if (!date) return [];
  return holidaysByDay.get(format(startOfDay(date), 'yyyy-MM-dd')) ?? [];
}

export function getDayNotes(
  date: Date | null,
  notesByDay: Map<string, CalendarNote[]>
): CalendarNote[] {
  if (!date) return [];
  return notesByDay.get(format(startOfDay(date), 'yyyy-MM-dd')) ?? [];
}

export function buildNotesByDay(
  notes: CalendarNote[]
): Map<string, CalendarNote[]> {
  const map = new Map<string, CalendarNote[]>();
  for (const note of notes) {
    if (!note.happensAt) continue;
    const day = startOfDay(parseISO(note.happensAt));
    const key = format(day, 'yyyy-MM-dd');
    const existing = map.get(key) ?? [];
    existing.push(note);
    map.set(key, existing);
  }
  return map;
}

export function buildTaskBadgesByDay(tasks: CalendarTask[]): Map<string, DayBadge[]> {
  const map = new Map<string, DayBadge[]>();
  const sorted = [...tasks].sort((a, b) => {
    const aTime = a.dueAt ? parseISO(a.dueAt).getTime() : Number.MAX_SAFE_INTEGER;
    const bTime = b.dueAt ? parseISO(b.dueAt).getTime() : Number.MAX_SAFE_INTEGER;
    return aTime - bTime;
  });

  for (const task of sorted) {
    if (!task.dueAt) continue;
    const key = format(startOfDay(parseISO(task.dueAt)), 'yyyy-MM-dd');
    const existing = map.get(key) ?? [];
    existing.push({
      id: task.id,
      kind: 'task',
      label: task.title,
    });
    map.set(key, existing);
  }

  return map;
}

export function getDayTasks(
  date: Date | null,
  tasksByDay: Map<string, CalendarTask[]>
): CalendarTask[] {
  if (!date) return [];
  return tasksByDay.get(format(startOfDay(date), 'yyyy-MM-dd')) ?? [];
}

export function buildTasksByDay(tasks: CalendarTask[]): Map<string, CalendarTask[]> {
  const map = new Map<string, CalendarTask[]>();
  const sorted = [...tasks].sort((a, b) => {
    const aTime = a.dueAt ? parseISO(a.dueAt).getTime() : Number.MAX_SAFE_INTEGER;
    const bTime = b.dueAt ? parseISO(b.dueAt).getTime() : Number.MAX_SAFE_INTEGER;
    return aTime - bTime;
  });

  for (const task of sorted) {
    if (!task.dueAt) continue;
    const key = format(startOfDay(parseISO(task.dueAt)), 'yyyy-MM-dd');
    const existing = map.get(key) ?? [];
    existing.push(task);
    map.set(key, existing);
  }
  return map;
}

export function buildOrgEventsByDay(
  events: OrgWideEvent[],
  visibleRange: DateRange
): Map<string, OrgWideEvent[]> {
  const map = new Map<string, OrgWideEvent[]>();

  for (const event of events) {
    const start = startOfDay(parseISO(event.startsAt));
    const end = startOfDay(parseISO(event.endsAt));
    addValueForEachDay(map, { start, end }, visibleRange, () => event);
  }

  return map;
}

export function buildHolidaysByDay(
  holidays: OrgHoliday[],
  visibleRange: DateRange
): Map<string, OrgHoliday[]> {
  const map = new Map<string, OrgHoliday[]>();

  for (const holiday of holidays) {
    for (const range of buildHolidayDateRanges(holiday, visibleRange)) {
      addValueForEachDay(map, range, visibleRange, () => holiday);
    }
  }

  return map;
}

export type ScheduleSlot = { label: string; title: string; minutes: number };

function compareAgendaItems(a: DayAgendaItem, b: DayAgendaItem): number {
  if (a.allDay && !b.allDay) return -1;
  if (!a.allDay && b.allDay) return 1;

  const aMinutes = extractMinutesFromLabel(a.timeLabel);
  const bMinutes = extractMinutesFromLabel(b.timeLabel);
  if (aMinutes !== bMinutes) return aMinutes - bMinutes;

  return a.title.localeCompare(b.title);
}

function extractMinutesFromLabel(label: string): number {
  const match = label.match(/(\d{1,2}):(\d{2})/);
  if (!match) return Number.MAX_SAFE_INTEGER;
  return Number(match[1]) * 60 + Number(match[2]);
}

function addBadgesForEachDay(
  map: Map<string, DayBadge[]>,
  candidateRange: DateRange,
  visibleRange: DateRange,
  getBadge: (day: Date) => DayBadge
) {
  const clipped = clipRange(candidateRange, visibleRange);
  if (!clipped) return;

  for (const day of eachDayOfInterval(clipped)) {
    const key = format(day, 'yyyy-MM-dd');
    const existing = map.get(key) ?? [];
    map.set(key, [...existing, getBadge(day)]);
  }
}

function addValueForEachDay<T>(
  map: Map<string, T[]>,
  candidateRange: DateRange,
  visibleRange: DateRange,
  getValue: (day: Date) => T
) {
  const clipped = clipRange(candidateRange, visibleRange);
  if (!clipped) return;

  for (const day of eachDayOfInterval(clipped)) {
    const key = format(day, 'yyyy-MM-dd');
    const existing = map.get(key) ?? [];
    map.set(key, [...existing, getValue(day)]);
  }
}

function clipRange(candidate: DateRange, visible: DateRange): DateRange | null {
  const start = isBefore(candidate.start, visible.start) ? visible.start : candidate.start;
  const end = isAfter(candidate.end, visible.end) ? visible.end : candidate.end;
  if (isAfter(start, end)) return null;
  return { start, end };
}

function buildHolidayDateRanges(holiday: OrgHoliday, visibleRange: DateRange): DateRange[] {
  const startsAt = startOfDay(parseISO(holiday.startsOn));
  const endsAt = startOfDay(parseISO(holiday.endsOn ?? holiday.startsOn));

  if (!holiday.yearlyRepeat) {
    return [{ start: startsAt, end: endsAt }];
  }

  const startMonth = startsAt.getMonth();
  const startDate = startsAt.getDate();
  const endMonth = endsAt.getMonth();
  const endDate = endsAt.getDate();
  const wrapsYear = endMonth < startMonth || (endMonth === startMonth && endDate < startDate);

  const yearStart = getYear(visibleRange.start) - 1;
  const yearEnd = getYear(visibleRange.end) + 1;
  const ranges: DateRange[] = [];

  for (let year = yearStart; year <= yearEnd; year += 1) {
    const start = startOfDay(new Date(year, startMonth, startDate));
    const end = startOfDay(new Date(wrapsYear ? year + 1 : year, endMonth, endDate));
    ranges.push({ start, end });
  }

  return ranges;
}

export function extractScheduleSlots(
  weeklyHours: Record<string, unknown> | null | undefined,
  date: Date
): ScheduleSlot[] {
  const dayName = format(date, 'EEEE').toLowerCase();
  const dayShort = format(date, 'EEE').toLowerCase();
  const dayNumber = String(date.getDay());
  if (!weeklyHours || typeof weeklyHours !== 'object') return [];

  const sourceValue = readDayHoursValue(weeklyHours, [dayName, dayShort, dayNumber]);
  if (!sourceValue) return [];

  const slots = normalizeDaySlots(sourceValue);
  return slots
    .filter((slot) => slot.isWorking)
    .map((slot) => {
      const label = slot.end ? `${slot.start} - ${slot.end}` : slot.start;
      return {
        label,
        title: slot.name ?? 'Work schedule',
        minutes: extractMinutesFromLabel(slot.start),
      };
    })
    .sort((a, b) => a.minutes - b.minutes);
}

function readDayHoursValue(weeklyHours: Record<string, unknown>, candidates: string[]): unknown {
  const entries = Object.entries(weeklyHours);
  for (const candidate of candidates) {
    const exact = entries.find(([key]) => key.toLowerCase() === candidate);
    if (exact) return exact[1];
  }
  return null;
}

type RawScheduleSlot = {
  start: string;
  end: string;
  name?: string;
  isWorking: boolean;
};

function normalizeDaySlots(value: unknown): RawScheduleSlot[] {
  if (Array.isArray(value)) {
    return value
      .map((entry) => normalizeSingleSlot(entry))
      .filter((entry): entry is RawScheduleSlot => Boolean(entry));
  }

  if (!value || typeof value !== 'object') return [];

  const record = value as Record<string, unknown>;
  if (record.closed === true || record.off === true || record.isWorking === false || record.isOpen === false) {
    return [];
  }

  if (Array.isArray(record.slots)) {
    return normalizeDaySlots(record.slots);
  }

  const single = normalizeSingleSlot(record);
  return single ? [single] : [];
}

function normalizeSingleSlot(value: unknown): RawScheduleSlot | null {
  if (typeof value === 'string') {
    const [startRaw, endRaw] = value.split('-').map((part) => part.trim());
    if (!startRaw) return null;
    return {
      start: startRaw,
      end: endRaw ?? '',
      isWorking: true,
    };
  }

  if (!value || typeof value !== 'object') return null;

  const record = value as Record<string, unknown>;
  if (record.closed === true || record.off === true || record.isWorking === false || record.isOpen === false) {
    return null;
  }

  const start = readFirstString(record, ['start', 'startTime', 'from', 'open']);
  const end = readFirstString(record, ['end', 'endTime', 'to', 'close']) ?? '';
  if (!start) return null;

  return {
    start,
    end,
    name: readFirstString(record, ['name', 'label', 'title']) ?? undefined,
    isWorking: true,
  };
}

function readFirstString(record: Record<string, unknown>, keys: string[]): string | null {
  for (const [recordKey, value] of Object.entries(record)) {
    if (!keys.some((key) => recordKey.toLowerCase() === key.toLowerCase())) continue;
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}
